import { BMCheckbox, BMPrimitives, BMText } from "@traveloka/district-bloom";
import { Pressable, StyleSheet } from "react-native";

import BMView from "../../View/BMView";
import { BMDropdownOptionsValue } from "../types";

export type BMDropdownGroupSelectRowProps<T> = {
  label: string;
  items: BMDropdownOptionsValue<T>[];
  selectedItems: BMDropdownOptionsValue<T>[];
  getValue: (value: T) => string;
  onPress: (nextSelectedItems: BMDropdownOptionsValue<T>[]) => void;
};

/**
 * Drives selection for an arbitrary subset of items ("this group" or, for
 * the whole-list convenience case, every item) via one checkbox row that
 * reflects and toggles just that subset within the overall selection.
 */
export default function BMDropdownGroupSelectRow<T>({
  label,
  items,
  selectedItems,
  getValue,
  onPress,
}: BMDropdownGroupSelectRowProps<T>) {
  const selectedValues = new Set(selectedItems.map((v) => getValue(v.value)));
  const selectedCount = items.filter((item) =>
    selectedValues.has(getValue(item.value))
  ).length;
  const allSelected = selectedCount === items.length && items.length > 0;
  const indeterminate = selectedCount > 0 && !allSelected;

  function handlePress() {
    const itemValues = new Set(items.map((item) => getValue(item.value)));
    const withoutGroup = selectedItems.filter(
      (v) => !itemValues.has(getValue(v.value))
    );
    onPress(allSelected ? withoutGroup : [...withoutGroup, ...items]);
  }

  return (
    <BMView>
      <Pressable onPress={handlePress}>
        <BMView
          row
          spacing="md"
          align="center"
          justify="between"
          style={styles.container}
        >
          <BMView row align="center">
            <BMView pointerEvents="none">
              <BMCheckbox value={allSelected} indeterminate={indeterminate} />
            </BMView>
            <BMText variant="uiLarge" style={styles.text}>
              {label}
            </BMText>
          </BMView>
        </BMView>
      </Pressable>
    </BMView>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: BMPrimitives.BMSize.md,
    paddingHorizontal: BMPrimitives.BMSize.lg,
  },
  text: {
    marginTop: BMPrimitives.BMSize.xs,
  },
});
